/**
 * Кнопка «На весь экран» (ISSUE-150). На телефоне Telegram полный экран просим сами один раз за сессию
 * (`requestFullscreenOnce`), а в Telegram для macOS, Desktop и Web игра открывается окном — там решает игрок.
 *
 * - Mini App на компьютере: `requestFullscreen()` / `exitFullscreen()` Telegram, состояние по `fullscreenChanged`,
 *   `fullscreenFailed` прячет кнопку (клиент не умеет). Отступы шапки Telegram на весь экран — `--app-inset-*` (start.ts).
 * - сайт вне Telegram: Fullscreen API браузера, только с мышью — на телефонах ничего не меняем.
 */
import { createStore } from "./state/create-store";
import { canToggleFullscreen, type TelegramWebApp } from "./telegram";

export type FullscreenMode = "telegram" | "browser";

export interface FullscreenState {
  /** `null` — кнопки нет. */
  mode: FullscreenMode | null;
  on: boolean;
}

export const fullscreenStore = createStore<FullscreenState>({ mode: null, on: false });

let toggle: (() => void) | null = null;

/** Нажатие кнопки: включить или выключить. */
export function toggleFullscreen(): void {
  toggle?.();
}

function hide(): void {
  toggle = null;
  fullscreenStore.set({ mode: null, on: false });
}

/** Mini App: кнопка, если это Telegram на компьютере или в вебе. Возвращает отписку. */
export function attachTelegramFullscreen(tg: TelegramWebApp): () => void {
  if (!canToggleFullscreen(tg)) return () => {};
  const sync = () => fullscreenStore.set({ mode: "telegram", on: Boolean(tg.isFullscreen) });
  const failed = () => hide();
  toggle = () => {
    try {
      if (tg.isFullscreen) tg.exitFullscreen!();
      else tg.requestFullscreen!();
    } catch {
      hide();
    }
  };
  const events: [string, () => void][] = [
    ["fullscreenChanged", sync],
    ["fullscreenFailed", failed],
  ];
  for (const [e, fn] of events) {
    try {
      tg.onEvent?.(e, fn);
    } catch {
      // старый клиент без событий
    }
  }
  sync();
  return () => {
    for (const [e, fn] of events) {
      try {
        tg.offEvent?.(e, fn);
      } catch {
        // нечего снимать
      }
    }
    hide();
  };
}

/** Что нужно от окна браузера (в тестах — подставное). */
export interface BrowserHost {
  document: Pick<
    Document,
    "fullscreenEnabled" | "fullscreenElement" | "exitFullscreen" | "addEventListener" | "removeEventListener"
  > & {
    documentElement: Pick<HTMLElement, "requestFullscreen">;
  };
  matchMedia: (query: string) => { matches: boolean };
}

/** Сайт: кнопка, если браузер умеет полный экран и управляют мышью (не телефон и не планшет). */
export function canBrowserFullscreen(win: BrowserHost): boolean {
  try {
    if (!win.document.fullscreenEnabled || typeof win.document.documentElement.requestFullscreen !== "function")
      return false;
    return win.matchMedia("(pointer: fine)").matches;
  } catch {
    return false;
  }
}

/** Сайт вне Telegram: Fullscreen API браузера. Возвращает отписку. */
export function attachBrowserFullscreen(win: BrowserHost = window): () => void {
  if (!canBrowserFullscreen(win)) return () => {};
  const doc = win.document;
  const sync = () => fullscreenStore.set({ mode: "browser", on: doc.fullscreenElement !== null });
  toggle = () => {
    const p = doc.fullscreenElement ? doc.exitFullscreen() : doc.documentElement.requestFullscreen();
    // отказ браузера (политика, iframe) — кнопка остаётся, состояние не меняется
    p?.catch(() => {});
  };
  doc.addEventListener("fullscreenchange", sync);
  sync();
  return () => {
    doc.removeEventListener("fullscreenchange", sync);
    hide();
  };
}
